import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Image, Spin, message } from 'antd';
import { useLazyQuery } from '@apollo/client';
import ImageUploader from '../../../../components/ImageUploader';
import { WORKBENCH_TASK_PHOTOS } from '../graphql/queries/workbenchTaskPhotos';

const MAX_PHOTO_COUNT = 6;

// 与 POD 一致：只上传到私有桶（storageUtils.fileUpload → getFileUploadUrl），
// 不用 getPublicFileUploadUrl —— 仓内作业照片不进公开桶
const UPLOAD_TAG = 'workbench-task';

interface ExistingPhoto {
  // 展示用地址，由 api-server 按 basic 回传的 s3Key 换成的短时效预签名 URL
  fileUrl?: string | null;
  fileName?: string | null;
  uploadedByName?: string | null;
  uploadedAt?: string | null;
}

interface TaskPhotoProofModalProps {
  open: boolean;
  taskTitle: string;
  requirePhotoProof: boolean;
  warehouseId?: number;
  taskId: number;
  submitting?: boolean;
  /** 已完结任务的查看态：只列已上传的留证，不提供上传与完结 */
  readOnly?: boolean;
  onCancel: () => void;
  onConfirm: (photoS3Keys: string[]) => void;
}

const TaskPhotoProofModal: React.FC<TaskPhotoProofModalProps> = ({
  open,
  taskTitle,
  requirePhotoProof,
  warehouseId,
  taskId,
  submitting,
  readOnly,
  onCancel,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [photoS3Keys, setPhotoS3Keys] = useState<string[]>([]);
  // 预签名 URL 有时效，每次打开都重新取，不缓存
  const [fetchPhotos, { data: photoData, loading: photoLoading }] = useLazyQuery(
    WORKBENCH_TASK_PHOTOS,
    { fetchPolicy: 'network-only' }
  );

  useEffect(() => {
    if (!open) return;
    setPhotoS3Keys([]);
    if (warehouseId) {
      fetchPhotos({ variables: { warehouseId, taskId } });
    }
  }, [open, warehouseId, taskId, fetchPhotos]);

  const existingPhotos: ExistingPhoto[] = photoData?.workbenchTaskPhotos ?? [];

  const totalCount = existingPhotos.length + photoS3Keys.length;

  const handleConfirm = () => {
    if (requirePhotoProof && totalCount === 0) {
      message.error(String(t('workbench.photoRequired')));
      return;
    }
    onConfirm(photoS3Keys);
  };

  return (
    <Modal
      open={open}
      title={String(t(readOnly ? 'workbench.viewPhotoProof' : 'workbench.photoProofTitle'))}
      okText={String(t('workbench.complete'))}
      cancelText={String(t(readOnly ? 'common.close' : 'common.cancel'))}
      okButtonProps={readOnly ? { style: { display: 'none' } } : undefined}
      confirmLoading={submitting}
      onCancel={onCancel}
      onOk={handleConfirm}
      destroyOnClose
    >
      <p className="text-sm font-medium text-gray-900 mb-1">{taskTitle}</p>
      {!readOnly && (
        <p className="text-xs text-gray-500 mb-3">
          {String(t(requirePhotoProof ? 'workbench.photoProofRequiredHint' : 'workbench.photoProofOptionalHint'))}
        </p>
      )}

      {photoLoading && (
        <div className="mb-3 flex justify-center">
          <Spin size="small" />
        </div>
      )}

      {existingPhotos.length > 0 && (
        <div className="mb-3">
          <p className="text-xs text-gray-500 mb-1.5">
            {String(t('workbench.photoProofUploaded', { count: existingPhotos.length }))}
          </p>
          {/* 包 PreviewGroup 才有左右翻页与计数，与 POD / 客诉附件的预览一致；
              缺了它每张图各成一组，放大后只能关掉再点下一张 */}
          <Image.PreviewGroup>
            <div className="flex gap-2 flex-wrap">
              {existingPhotos.map((photo, idx) => (
                <Image
                  key={photo.fileUrl ?? idx}
                  src={photo.fileUrl ?? ''}
                  width={64}
                  height={64}
                  className="object-cover rounded border border-violet-200"
                />
              ))}
            </div>
          </Image.PreviewGroup>
        </div>
      )}

      {readOnly ? (
        !photoLoading && existingPhotos.length === 0 && (
          <p className="text-sm text-gray-400">{String(t('workbench.noPhotoProof'))}</p>
        )
      ) : (
        <ImageUploader
          value={photoS3Keys}
          onChange={setPhotoS3Keys}
          maxCount={MAX_PHOTO_COUNT - existingPhotos.length}
          tag={UPLOAD_TAG}
          disabled={submitting}
        />
      )}
    </Modal>
  );
};

export default TaskPhotoProofModal;
